import * as React from 'react';
import {Link} from 'react-router-dom';
import {CellCustomDataType, ColumnType} from 'ted-ui/build/Table';
import { Application } from '@applications/order-tracker';
import { Links, Components } from '@telstra-connect/order-tracker/activity-tracking';
import StaticData from 'ted-ui/build/StaticData';
import Lozenge from '@able/react/dist/Lozenge';
import {getOrderDetailsRoutePath, getProductSheetRoutePath} from '@applications/order-tracker/utils';
import {validateProductFields} from './order-products-utils';
import {ProductSheetTabRoutes, ProductStatus, VariantProgressMapping} from './constants';
import { IProductOrder } from '@applications/order-tracker/config/order-tracker-new-common/types'
import { isSourceMpex } from '@telstra-connect/order-tracker/methods/order-tracker-uplifted';

/**
 * This function takes an array of column definitions and customizes their cell rendering logic.
 * It returns a new array of column definitions with updated Cell properties.
 *
 * @param {ColumnType[]} columns - The original array of column definitions.
 * @returns {ColumnType[]} - A new array of column definitions with customized Cell rendering functions.
 */
export const customizeTableCell = (
    columns: ColumnType[],
    isProductLinkInActive: boolean,
    onOrderClick?: (origin: IProductOrder, event: React.MouseEvent<HTMLElement>) => boolean
) => {
    // Import validation functions for cleaner code (assuming they are defined elsewhere)
    const {
        validateSiteName,
        validateEstimatedCompletionDate,
        validateUpcomingAppointment,
        validateServiceId,
        validateSiteAddress
    } = validateProductFields();

    // Function to render estimated completion date cell with lozenge based on progress
    const renderEstimatedCompletionDateCell = ({ value, origin }: CellCustomDataType) => {
        const normalizedStatus = origin.status.toLowerCase();
        const normalizedProgress = origin.productProgress.toLowerCase();
        if (normalizedStatus === ProductStatus.Closed || normalizedStatus === ProductStatus.Cancelled || !validateEstimatedCompletionDate(value)) {
            return (
                <Lozenge
                    label={origin.productProgress}
                    variant={VariantProgressMapping[normalizedProgress]}
                />
            );
        } else {
            return (
                <StaticData
                    name={origin.productProgress}
                    standAlone
                    value={value}
                    lozenge={{
                        label: origin.productProgress,
                        variant: VariantProgressMapping[normalizedProgress]
                    }}
                />
            )
        }
    };

    const sanitizeLink = (url: string): URL | undefined => {
        try {
            return new URL(`${window.location.origin}/${url}`);
        } catch (error) {
            return;
        }
    };

    const trackProductLinkClick = (tab: string): void => {
        Application.tracker.trackLink(
            Links.productDetails[tab],
            Components.productDetailsPage
        );
    };

    // Function to render link cells for product details and progress tracker
    const renderLinkCell = (cellInfo: CellCustomDataType, tab: string) => {
        const { origin, value } = cellInfo;
        const { productOrderNumber, sourceSystem } = origin;
        const link = getProductSheetRoutePath(productOrderNumber, tab);
        const sanitizedLink = sanitizeLink(link)
        const path = `${sanitizedLink.pathname.replace('/', '')}`;

        const handleLinkClick = (event: React.BaseSyntheticEvent) => {
            event?.target?.classList?.add('custom-link');
            trackProductLinkClick(tab);
        };

        return (
            <React.Fragment>
                {(isProductLinkInActive && isSourceMpex(sourceSystem)) ? (
                    <React.Fragment>{value}</React.Fragment>
                ) : (
                    <Link
                        className={`product-link`}
                        key={productOrderNumber}
                        to={path}
                        onClick={handleLinkClick}
                    >
                        {value}
                    </Link>
                )}
            </React.Fragment>
        );
    };

    // Function to render link cells for order number column of order list table
    const renderOrderNumberCell = (cellInfo: CellCustomDataType) => {
        const { origin, value } = cellInfo;
        const {cidn, orderNumber, productOrderNumber} = origin;
        const link = getOrderDetailsRoutePath(cidn, orderNumber, true);
        const sanitizedLink = sanitizeLink(link)
        const path = `${sanitizedLink.pathname.replace('/', '')}`;

        const handleClick = (e: React.MouseEvent<HTMLElement>) => {
            const shouldPreventNavigation = onOrderClick(origin, e);
            if (shouldPreventNavigation) {
                e.preventDefault();
            }
        };

        return (
            <Link
                className="product-link"
                to={path}
                key={productOrderNumber}
                onClick={handleClick}
            >
                {value}
            </Link>
        );
    };

    // Loop through each column and customize its Cell rendering based on accessor
    return columns.map((column: ColumnType) => {
        switch (column.accessor) {
            case 'siteName':
                return {
                    ...column,
                    Cell: ({ value }: CellCustomDataType) => <>{validateSiteName(value)}</>
                }
            case 'productName':
                return {
                    ...column,
                    Cell: (cellInfo: CellCustomDataType) =>
                        renderLinkCell(cellInfo, ProductSheetTabRoutes.ProductDetails),
                };
            case 'estimatedCompletionDate':
                return {
                    ...column,
                    Cell: (cellInfo: CellCustomDataType) =>
                        renderEstimatedCompletionDateCell(cellInfo)
                };
            case 'progressTrackerStatus':
                return {
                    ...column,
                    Cell: (cellInfo: CellCustomDataType) =>
                        renderLinkCell(cellInfo, ProductSheetTabRoutes.ProgressTracker),
                };
            case 'upcomingAppointmentDate':
                return {
                    ...column,
                    Cell: ({ value }: CellCustomDataType) => <>{validateUpcomingAppointment(value)}</>
                };
            case 'serviceId':
                return {
                    ...column,
                    Cell: ({ value }: CellCustomDataType) => <>{validateServiceId(value)}</>
                };
            case 'siteRawAddress':
                return {
                    ...column,
                    Cell: ({ value }: CellCustomDataType) => <>{validateSiteAddress(value)}</>
                };
            case 'orderNumber':
                return {
                    ...column,
                    Cell: (cellInfo: CellCustomDataType) =>
                        renderOrderNumberCell(cellInfo),
                };
            default:
                return column;
        }
    });
}
